// Smart search (REG-742): one engine behind every search box.
//
// Every box calls the active SmartSearchBackend: the search API (REG-881)
// when VITE_SEARCH_API_URL is set, else the Postgres RPC public.smart_search.
// The API picks the engine itself (search.config.live_source: Typesense's
// queue or CDC copy, or Postgres) and re-checks every hit under the caller's
// RLS, so both backends answer the same rows. When the API cannot be reached
// or fails, the box asks the RPC instead, and skips the API for a minute.
import { callRpc } from './supabase-operations';
import { supabaseClient } from './supabase';

export type SmartEntity = 'merchant' | 'deal' | 'lead' | 'rep' | 'funder' | 'lead_source' | 'email';

/**
 * Every type, for the header search and /search. Leads must be asked for by
 * name (an empty list means everything but leads); they come only from the
 * search API, for users with lead_import, and never from the Postgres RPC.
 */
export const ALL_SMART_ENTITIES: SmartEntity[] = ['merchant', 'deal', 'lead', 'rep', 'funder', 'lead_source'];

/**
 * The header search's types: every type, plus Gmail and Instantly emails
 * (REG-885) for users who can open the email archive. The search API checks
 * the same permission and answers emails to nobody else; asking only for
 * those users saves the others a wasted lookup.
 */
export function headerSearchEntities(canViewEmailArchive: boolean): SmartEntity[] {
  return canViewEmailArchive ? [...ALL_SMART_ENTITIES, 'email'] : ALL_SMART_ENTITIES;
}

export type SmartMatchKind = 'exact' | 'partial' | 'fuzzy' | 'weak';
export type SmartMatchedField =
  | 'name' | 'dba' | 'owner' | 'phone' | 'email' | 'number' | 'id'
  // Email hits (REG-885).
  | 'subject' | 'from' | 'to' | 'text' | 'lead';

export interface SmartHit {
  section: 'match' | 'suggestion';
  rank: number;
  entity: SmartEntity;
  entity_id: number;
  /** What a click opens: a deal opens its merchant. */
  merchant_id: number | null;
  title: string;
  submission_number: number | null;
  funder_name: string | null;
  status: string | null;
  matched_field: SmartMatchedField;
  matched_value: string | null;
  match_kind: SmartMatchKind;
  edit_distance: number;
  tier: number;
  /** Email hits only (entity 'email', from the search API). */
  email_source?: 'gmail' | 'instantly';
  sender?: string | null;
  recipient?: string | null;
  snippet?: string | null;
  sent_at?: string | null;
  /** The archive conversation a Gmail hit opens. */
  email_thread?: number | null;
  mailbox?: string | null;
}

export interface SmartRecent {
  entity: SmartEntity;
  entity_id: number;
  merchant_id: number | null;
  title: string;
  submission_number: number | null;
  funder_name: string | null;
  status: string | null;
  opened_at: string;
}

export interface SmartSearchBackend {
  search(query: string, entities: SmartEntity[] | null, limit: number): Promise<SmartHit[]>;
  recent(limit: number): Promise<SmartRecent[]>;
  recordSelection(query: string, hit: Pick<SmartHit, 'entity' | 'entity_id' | 'merchant_id'>): Promise<void>;
}

export const postgresSmartSearch: SmartSearchBackend = {
  async search(query, entities, limit) {
    const { data } = await callRpc({
      schema: 'public',
      functionName: 'smart_search',
      params: { p_query: query, p_types: entities, p_limit: limit },
    });
    return (data ?? []) as SmartHit[];
  },
  async recent(limit) {
    const { data } = await callRpc({ schema: 'public', functionName: 'smart_search_recent', params: { p_limit: limit } });
    return (data ?? []) as SmartRecent[];
  },
  async recordSelection(query, hit) {
    await callRpc({
      schema: 'public',
      functionName: 'smart_search_record_selection',
      params: { p_query: query, p_entity: hit.entity, p_entity_id: hit.entity_id, p_merchant_id: hit.merchant_id },
    });
  },
};

const SEARCH_API_TIMEOUT_MS = 2500;
const SEARCH_API_RETRY_MS = 60_000;
let searchApiDownUntil = 0;

/** POST /search on the search API with the user's token; null when it is unreachable or fails. */
async function searchApi(base: string, query: string, entities: SmartEntity[] | null, limit: number): Promise<SmartHit[] | null> {
  if (Date.now() < searchApiDownUntil) return null;
  const { data: { session } } = await supabaseClient.auth.getSession();
  if (!session) return null;
  try {
    const response = await fetch(`${base}/search`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, types: entities ?? [], limit }),
      signal: AbortSignal.timeout(SEARCH_API_TIMEOUT_MS),
    });
    if (!response.ok) throw new Error(`search API ${response.status}`);
    const body = (await response.json()) as { hits: SmartHit[] };
    return body.hits;
  } catch (error) {
    searchApiDownUntil = Date.now() + SEARCH_API_RETRY_MS;
    console.warn('smart search: search API unavailable, using Postgres', error);
    return null;
  }
}

export function apiSmartSearch(base: string): SmartSearchBackend {
  return {
    async search(query, entities, limit) {
      return (await searchApi(base, query, entities, limit)) ?? postgresSmartSearch.search(query, entities, limit);
    },
    recent: (limit) => postgresSmartSearch.recent(limit),
    recordSelection: (query, hit) => postgresSmartSearch.recordSelection(query, hit),
  };
}

/** The search API's base URL (REG-881), or undefined when the app reads Postgres directly. */
export const searchApiUrl: string | undefined = import.meta.env.VITE_SEARCH_API_URL?.replace(/\/$/, '');

export const smartSearchBackend: SmartSearchBackend = searchApiUrl ? apiSmartSearch(searchApiUrl) : postgresSmartSearch;

export const ENTITY_LABELS: Record<SmartEntity, string> = {
  merchant: 'Merchants',
  deal: 'Deals',
  lead: 'Leads',
  rep: 'Reps',
  funder: 'Funders',
  lead_source: 'Lead sources',
  email: 'Email',
};

/** "13055551234" / "3055551234" -> "(305) 555-1234"; anything else unchanged. */
export function formatPhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return value ?? '';
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/** The one detail shown after a merchant's name: only the phone or email that matched. */
export function matchedDetail(hit: Pick<SmartHit, 'matched_field' | 'matched_value'>): string | null {
  if (hit.matched_field === 'phone') return formatPhone(hit.matched_value);
  if (hit.matched_field === 'email') return hit.matched_value;
  if (hit.matched_field === 'owner' || hit.matched_field === 'dba') return hit.matched_value;
  return null;
}

/** A badge only for matches that are not exact, e.g. "Similar name", "Phone off by 1". */
export function matchBadge(hit: Pick<SmartHit, 'match_kind' | 'matched_field' | 'edit_distance'>): string | null {
  if (['subject', 'from', 'to', 'text', 'lead'].includes(hit.matched_field)) {
    return hit.match_kind === 'fuzzy' || hit.match_kind === 'weak' ? 'Similar words' : null;
  }
  if (hit.match_kind === 'exact' || hit.match_kind === 'partial') {
    if (hit.matched_field === 'owner') return 'Owner';
    if (hit.matched_field === 'dba') return 'DBA';
    return null;
  }
  const n = hit.edit_distance;
  switch (hit.matched_field) {
    case 'phone':
      return n > 0 ? `Phone off by ${n}` : 'Similar phone';
    case 'email':
      return n > 0 ? `Email off by ${n}` : 'Similar email';
    case 'owner':
      return 'Similar owner';
    case 'dba':
      return 'Similar DBA';
    default:
      return 'Similar name';
  }
}

/** "Oct 28" this year, "Oct 28, 2025" before; empty when unknown. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

/**
 * Where an email hit opens: a Gmail message opens its conversation in Email
 * Records (that company's tab; the page looks the conversation up by id); an
 * Instantly email opens its merchant when it has one the user can see, else
 * Opportunities on the Instantly tab.
 */
export function emailHitTarget(hit: Pick<SmartHit, 'email_source' | 'email_thread' | 'mailbox' | 'merchant_id'>):
  { kind: 'merchant'; merchantId: number } | { kind: 'path'; path: string } {
  if (hit.email_source === 'instantly') {
    if (hit.merchant_id != null) return { kind: 'merchant', merchantId: hit.merchant_id };
    return { kind: 'path', path: '/opportunities?channel=instantly' };
  }
  const params = new URLSearchParams();
  const domain = (hit.mailbox ?? '').trim().toLowerCase().split('@')[1];
  if (domain) params.set('company', domain);
  if (hit.email_thread != null) params.set('thread', String(hit.email_thread));
  const query = params.toString();
  return { kind: 'path', path: query ? `/email-records?${query}` : '/email-records' };
}

/** Group hits by entity, in the order of each group's best rank. */
export function groupHits(hits: SmartHit[]): { entity: SmartEntity; hits: SmartHit[] }[] {
  const groups = new Map<SmartEntity, SmartHit[]>();
  for (const hit of hits) {
    const list = groups.get(hit.entity) ?? [];
    list.push(hit);
    groups.set(hit.entity, list);
  }
  return [...groups.entries()]
    .map(([entity, list]) => ({ entity, hits: list.sort((a, b) => a.rank - b.rank) }))
    .sort((a, b) => a.hits[0].rank - b.hits[0].rank);
}

/** Which smart-search types a list of this table should offer, or null for none. */
export function smartEntitiesForTable(tableName: string | null | undefined): SmartEntity[] | null {
  switch (tableName) {
    case 'merchant':
      return ['merchant', 'deal'];
    case 'submission':
      return ['deal', 'merchant'];
    case 'rep':
      return ['rep'];
    case 'funder':
      return ['funder'];
    case 'lead_source':
      return ['lead_source'];
    default:
      return null;
  }
}

export type SmartRow =
  | { kind: 'hit'; hit: SmartHit }
  | { kind: 'recent'; recent: SmartRecent };

/** Rows in keyboard order: matches by group, then "did you mean". */
export function orderRows(hits: SmartHit[], recents: SmartRecent[] = []): SmartRow[] {
  if (hits.length === 0) return recents.map((recent) => ({ kind: 'recent', recent }));
  const matches = groupHits(hits.filter((h) => h.section === 'match'));
  const suggestions = hits.filter((h) => h.section === 'suggestion').sort((a, b) => a.rank - b.rank);
  return [
    ...matches.flatMap((g) => g.hits.map((hit) => ({ kind: 'hit' as const, hit }))),
    ...suggestions.map((hit) => ({ kind: 'hit' as const, hit })),
  ];
}
